import { v2 } from "@pufflig/prompt-studio-types";
import { CreateRunbookOptions } from "./types";

/**
 * Builds a runbook that outlines the steps to run and their interdependencies.
 *
 * The function traverses the provided steps and collects all steps that should be executed.
 * A step is included in the runbook if:
 * - The step does not have an existing result in `stepResults`, or
 * - The `resetResults` option is set to true (in which case the step is included regardless).
 *
 * When a `stepId` is specified in the options, traversal begins from that step.
 * Otherwise, all steps are examined. For each step, the function inspects its `references`
 * property and, for every reference of type "step", records the dependency between the current
 * step (contextId) and the referenced step (variableId). If the referenced step does not have a result
 * (or if `resetResults` is true), its dependencies are recursively processed.
 *
 * @param steps - A mapping of step IDs to their corresponding QueryStep objects.
 * @param stepResults - A set of step IDs that have results.
 * @param options - Options for creating the runbook, which may include:
 *    - stepId: the starting step ID for traversal.
 *    - resetResults: if true, include steps regardless of whether they have results.
 * @returns A Runbook object containing:
 *    - steps: an object mapping step IDs (to be run) to themselves.
 *    - references: an array of dependency objects, each with a contextId (the originating step)
 *      and a variableId (the referenced step).
 */
export function getRunGraph(
  steps: Record<string, v2.QueryStep>,
  stepResults: Set<string>,
  options?: CreateRunbookOptions,
): v2.Runbook {
  const { stepId, resetResults } = options || {};
  const runbook: v2.Runbook = { steps: {}, references: [] };
  const visited = new Set<string>();

  async function traverse(currentStepId: string) {
    if (visited.has(currentStepId)) return;
    visited.add(currentStepId);

    // Check if we should include this step:
    // - if resetResults is true, include regardless of any result
    // - otherwise, only include if no result exists
    const hasResult = stepResults.has(currentStepId);
    if (resetResults || !hasResult || stepId === currentStepId) {
      runbook.steps[currentStepId] = {
        args: {
          context: "",
          query: steps[currentStepId].query,
        },
        type: steps[currentStepId].type,
      };
    }

    const step = steps[currentStepId];
    if (!step) return; // if the step is not found, do nothing

    // Iterate over all references in the step
    for (const ref of step.references) {
      // Only process references of type "step"
      if (ref.type === "step") {
        const referencedStepId = ref.sourceId;
        // Record the dependency between the current step and the referenced step
        runbook.references.push({
          contextId: currentStepId,
          variableId: referencedStepId,
        });
        // If the referenced step doesn't have a result (or if we're resetting results),
        // traverse its dependencies recursively.
        const refHasResult = stepResults.has(referencedStepId);
        if (resetResults || !refHasResult) {
          traverse(referencedStepId);
        }
      }
    }
  }

  // If a starting stepId is provided, only traverse from that step,
  // otherwise start with all steps.
  if (stepId) {
    traverse(stepId);
  } else {
    for (const key in steps) {
      const stepHasResult = stepResults.has(key);
      if (resetResults || !stepHasResult) {
        traverse(key);
      }
    }
  }

  return runbook;
}
